import "@mantine/dates/styles.css";
import AOS from "aos";
import "aos/dist/aos.css";
import "@mantine/carousel/styles.css";
import { useEffect } from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import "./App.css";
import Home from "./Components/Home";
import Layout from "./Layout";
import BlogsLayout from "./Components/Blogs/BlogsLayout";
import Blogs from "./Components/Blogs/Blogs";
import BlogViewer from "./Components/Blogs/BlogViewer";
import AboutUs from "./Components/AboutUs";
import AssignmentHelpIndexPage from "./Components/AssignmentHelp/AssignmentHelpIndexPage";
import { assignmentPageComponentMap } from "./Components/AssignmentHelp/assignments/assignmentPageComponentRegistry";
import CityPage from "./Components/CityPage";
import CountryPage from "./Components/CountryPage";
import ContactPage from "./Components/ContactPage";
import TermsAndService from "./Components/TermsAndService";
import PrivacyPolicy from "./Components/PrivacyPolicy";
import ReviewsPage from "./Components/Reviews/ReviewsPage";
import ServiceLandingPage from "./Components/Services/ServiceLandingPage";
import OrderFormPage from "./Components/Shared/OrderFormPage";
import ApushScoreCalculatorPage from "./Components/Tools/ApushScoreCalculatorPage";
import ApBioScoreCalculatorPage from "./Components/Tools/ApBioScoreCalculatorPage";
import CatalogToolInfoPage from "./Components/Tools/CatalogToolInfoPage";
import CursiveGeneratorPage from "./Components/Tools/CursiveGeneratorPage";
import FinalGradeCalculatorPage from "./Components/Tools/FinalGradeCalculatorPage";
import GradeCalculatorPage from "./Components/Tools/GradeCalculatorPage";
import GpaCalculatorPage from "./Components/Tools/GpaCalculatorPage";
import {
  PagesToWordsCalculatorPage,
  WordsToPagesCalculatorPage,
} from "./Components/Tools/PageCountCalculatorPages";
import RacineCarreeCalculatorPage from "./Components/Tools/RacineCarreeCalculatorPage";
import ReadingTimeCalculatorPage from "./Components/Tools/ReadingTimeCalculatorPage";
import SpeechTimeCalculatorPage from "./Components/Tools/SpeechTimeCalculatorPage";
import StudentUtilityToolPage, {
  getStudentUtilityToolRoutes,
} from "./Components/Tools/StudentUtilityToolPage";
import MathToolPlaceholderPage from "./Components/Tools/MathToolPlaceholderPage";
import ToolPlaceholderPage from "./Components/Tools/ToolPlaceholderPage";
import ToolsIndexPage from "./Components/Tools/ToolsIndexPage";
import NotFound from "./Components/NotFound";
import {
  getAssignmentPageBySlug,
  getAssignmentPageRoutes,
} from "./data/assignmentPages";
import {
  getBlogByTitle,
  getBlogRoutes,
  getStaticBlogs,
} from "./Services/blog.service";
import {
  ASSIGNMENT_HELP_INDEX_PATH,
  BLOGS_INDEX_PATH,
  getAssignmentPagePath,
  getBlogPath,
} from "./utils/routes";

const assignmentRoutes = getAssignmentPageRoutes();
const staticBlogs = getStaticBlogs();
const blogRoutes = getBlogRoutes();
const catalogInfoToolSlugs = [
  "assignment-grade-calculator",
  "college-gpa-calculator",
  "grammar-checker",
  "paraphrasing-tool",
  "word-counter",
  "essay-title-generator",
  "square-root-calculator",
  "percentage-calculator",
  "scientific-calculator",
  "fraction-calculator",
  "quadratic-formula-calculator",
  "ap-chem-score-calculator",
  "ap-lang-score-calculator",
  "ap-csp-score-calculator",
  "ap-psych-score-calculator",
  "ap-human-geography-score-calculator",
  "ap-grading-scale",
];

function LegacyAssignmentRedirect() {
  const { slug } = useParams();
  const page = getAssignmentPageBySlug(slug);

  return (
    <Navigate
      to={page ? getAssignmentPagePath(page.slug) : ASSIGNMENT_HELP_INDEX_PATH}
      replace
    />
  );
}

function LegacyBlogRedirect() {
  const { title } = useParams();
  const blog = getBlogByTitle(staticBlogs, title);

  return (
    <Navigate to={blog ? getBlogPath(blog.slug) : BLOGS_INDEX_PATH} replace />
  );
}

function App() {
  useEffect(() => {
    AOS.init({ duration: 800, offset: 300 });
  }, []);

  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        <Route index element={<Home />} />
        <Route path="about-us" element={<AboutUs />} />
        <Route path="contact" element={<ContactPage />} />
        <Route path="reviews" element={<ReviewsPage />} />
        <Route
          path="essay-writing-help"
          element={<Navigate to="/services/essay-editing-help" replace />}
        />
        <Route
          path="research-paper-help"
          element={<Navigate to="/assignment-help" replace />}
        />
        <Route
          path="math-assignment-help"
          element={<Navigate to="/services/math-assignment-help" replace />}
        />
        <Route
          path="nursing-assignment-help"
          element={<Navigate to="/healthcare-assignment-help/" replace />}
        />
        <Route
          path="engineering-assignment-help"
          element={<Navigate to="/assignment-help" replace />}
        />
        <Route
          path="informative-speech-topics"
          element={<Navigate to="/topics-for-informative-speech/" replace />}
        />
        <Route
          path="robotics-essay"
          element={
            <Navigate
              to="/robotics-vs-ai/#robotics-vs-ai-essay-assignment-points"
              replace
            />
          }
        />
        <Route
          path="presentation-help"
          element={<Navigate to="/services/speech-writing-help" replace />}
        />
        <Route
          path="study-help"
          element={<Navigate to="/assignment-help" replace />}
        />
        <Route
          path="homework-help"
          element={<Navigate to="/assignment-help" replace />}
        />
        <Route
          path="exam-help"
          element={<Navigate to="/assignment-help" replace />}
        />
        <Route path="assignment-help">
          <Route index element={<AssignmentHelpIndexPage />} />
          <Route path=":slug/*" element={<LegacyAssignmentRedirect />} />
        </Route>
        <Route path="order-form" element={<OrderFormPage />} />
        {assignmentRoutes.map((route) => {
          const AssignmentRouteComponent = assignmentPageComponentMap[route.path];

          if (!AssignmentRouteComponent) {
            return null;
          }

          return (
            <Route
              key={route.path}
              path={route.path}
              element={<AssignmentRouteComponent />}
            />
          );
        })}
        <Route path="blogs" element={<BlogsLayout />}>
          <Route index element={<Blogs />} />
          <Route path=":title" element={<LegacyBlogRedirect />} />
        </Route>
        <Route path="blog" element={<Navigate to={BLOGS_INDEX_PATH} replace />} />
        <Route path="blog/:title" element={<LegacyBlogRedirect />} />
        {blogRoutes.map((route) => (
          <Route key={route.path} path={route.path} element={<BlogsLayout />}>
            <Route index element={<BlogViewer />} />
          </Route>
        ))}
        <Route path="tools">
          <Route index element={<ToolsIndexPage />} />
          <Route
            path="cursive-generator"
            element={<CursiveGeneratorPage />}
          />
          <Route path="grade-calculator" element={<GradeCalculatorPage />} />
          <Route
            path="final-grade-calculator"
            element={<FinalGradeCalculatorPage />}
          />
          <Route path="gpa-calculator" element={<GpaCalculatorPage />} />
          <Route
            path="weighted-grade-calculator"
            element={
              <Navigate
                to="/tools/grade-calculator#weighted-grade-calculator"
                replace
              />
            }
          />
          <Route
            path="test-grade-calculator"
            element={
              <Navigate
                to="/tools/grade-calculator#test-grade-calculator"
                replace
              />
            }
          />
          <Route
            path="words-to-pages-calculator"
            element={<WordsToPagesCalculatorPage />}
          />
          <Route
            path="pages-to-words-calculator"
            element={<PagesToWordsCalculatorPage />}
          />
          <Route
            path="racine-carree-calculator"
            element={<RacineCarreeCalculatorPage />}
          />
          <Route
            path="reading-time-calculator"
            element={<ReadingTimeCalculatorPage />}
          />
          <Route
            path="read-time-calculator"
            element={<Navigate to="/tools/reading-time-calculator" replace />}
          />
          <Route
            path="how-long-to-read-calculator"
            element={<Navigate to="/tools/reading-time-calculator" replace />}
          />
          <Route
            path="speech-time-calculator"
            element={<SpeechTimeCalculatorPage />}
          />
          <Route
            path="ap-bio-score-calculator"
            element={<ApBioScoreCalculatorPage />}
          />
          <Route
            path="apush-score-calculator"
            element={<ApushScoreCalculatorPage />}
          />
          {getStudentUtilityToolRoutes().map((slug) => (
            <Route
              key={slug}
              path={slug}
              element={<StudentUtilityToolPage slug={slug} />}
            />
          ))}
          {catalogInfoToolSlugs.map((slug) => (
            <Route
              key={slug}
              path={slug}
              element={<CatalogToolInfoPage slug={slug} />}
            />
          ))}
          <Route
            path="square-and-square-root-calculator"
            element={
              <MathToolPlaceholderPage
                title="Square and Square Root Calculator"
                description="Use this Square and Square Root Calculator page to compare a number, its square, and its square root."
                path="/tools/square-and-square-root-calculator"
              />
            }
          />
          <Route
            path="assignment-word-count-calculator"
            element={
              <ToolPlaceholderPage
                title="Assignment Word Count Calculator"
                description="Use this Assignment Word Count Calculator placeholder page to browse related academic writing tools."
                path="/tools/assignment-word-count-calculator"
                category="Writing Tool"
                relatedPath="/tools/cursive-generator"
                relatedLabel="Open Cursive Generator"
              />
            }
          />
        </Route>
        <Route path="uk">
          <Route index element={<CountryPage />} />
          <Route path=":city" element={<CityPage />} />
          <Route path=":city/assignment-help" element={<CityPage />} />
          <Route path="assignment-help-:city" element={<CityPage />} />
        </Route>
        <Route path=":country">
          <Route index element={<CountryPage />} />
          <Route path=":city/assignment-help" element={<CityPage />} />
          <Route path="assignment-help-:city" element={<CityPage />} />
        </Route>
        <Route path="services/:slug" element={<ServiceLandingPage />} />
        <Route path="terms-of-service" element={<TermsAndService />} />
        <Route path="privacy-policy" element={<PrivacyPolicy />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

export default App;
